// Admin-only "Reassign" control for a lead row.
//
// Sources its options from the `teamMembers` Outlet context (UID-keyed user
// accounts — `assignedBrokerId` IS a user uid, NOT a BigQuery commission
// hash). Filters to broker-family roles that are active, and — for
// office-scoped admins — to members whose officeIds intersect the caller's
// scope. The server (leadAction.updateLead) is the real authority on
// out-of-scope target offices; this picker just avoids offering impossible
// choices.
//
// On confirm: updateLead({ assignedBrokerId, assignedBrokerName }). The
// backend appends a `manual_admin` assignmentHistory entry, resets the SLA
// window, and fires the assignment email.
import React, { useMemo, useState } from 'react';
import { RefreshCw, UserCog } from 'lucide-react';

export interface ReassignTeamMember {
  id: string;
  displayName?: string;
  role?: string;
  status?: string;
  officeIds?: string[];
  email?: string;
}

const BROKER_FAMILY_ROLES = new Set(['broker', 'broker_assistant', 'credit_analyst']);

export const ReassignLeadControl: React.FC<{
  currentAssignedBrokerId: string | null;
  teamMembers: ReassignTeamMember[];
  // When the caller is an office-scoped admin, restrict the picker to members
  // whose officeIds intersect these. Undefined = full company scope (no filter).
  scopeOfficeIds?: string[];
  onReassign: (member: { id: string; displayName: string }) => Promise<void>;
}> = ({ currentAssignedBrokerId, teamMembers, scopeOfficeIds, onReassign }) => {
  const [busy, setBusy] = useState(false);

  const options = useMemo(() => {
    return teamMembers
      .filter((m) => m.role && BROKER_FAMILY_ROLES.has(m.role))
      .filter((m) => m.status !== 'inactive')
      .filter((m) => {
        if (!scopeOfficeIds || scopeOfficeIds.length === 0) return true;
        const mine = m.officeIds ?? [];
        return mine.some((o) => scopeOfficeIds.includes(o));
      })
      .map((m) => ({
        id: m.id,
        displayName: m.displayName || m.email || m.id,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [teamMembers, scopeOfficeIds]);

  if (options.length === 0) return null;

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    if (!id) return;
    const member = options.find((o) => o.id === id);
    if (!member) return;
    setBusy(true);
    try {
      await onReassign(member);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-1">
      <UserCog className="w-3 h-3 text-gray-400" />
      <select
        value={currentAssignedBrokerId ?? ''}
        onChange={handleChange}
        disabled={busy}
        title="Reassign this lead to another broker"
        className="px-2 py-1 rounded-md bg-gray-800 border border-gray-700 text-white text-xs focus:outline-none focus:border-blue-500 disabled:opacity-50"
      >
        <option value="">Reassign…</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.displayName}
          </option>
        ))}
      </select>
      {busy && <RefreshCw className="w-3 h-3 animate-spin text-gray-400" />}
    </span>
  );
};

export default ReassignLeadControl;
